import Chart, {
  ArgumentAxis,
  CommonSeriesSettings,
  Export,
  Legend,
  Series,
  Tooltip,
  ValueAxis
} from 'devextreme-react/chart';
import PieChart, {
  Connector,
  Export as PieExport,
  Label,
  Legend as PieLegend,
  Series as PieSeries,
  Tooltip as PieTooltip
} from 'devextreme-react/pie-chart';
import type { ChartThemeColors } from '../charts/useChartThemeColors';
import { formatReportingChartDayLabel } from './formatters';
import type {
  UserActivityDailySessionCount,
  UserActivityDailyUniqueUsers,
  UserActivityDimensionRow,
  UserActivityDurationBand,
  UserActivityHourBucket,
  UserActivityNamedCount,
  UserActivityReportMetric,
  UserActivityTrendPoint,
  UserActivityWeekdayBucket,
  UserActivityWeeklyBucket
} from './types';

function weekLabel(weekStart: string): string {
  return `Week of ${formatReportingChartDayLabel(weekStart)}`;
}

function metricCaption(metric: UserActivityReportMetric): string {
  if (metric === 'uniqueUsers') return 'Unique users';
  if (metric === 'totalMinutes') return 'Total minutes';
  return 'Sessions';
}

function dimensionMetricValue(row: UserActivityDimensionRow, metric: UserActivityReportMetric): number {
  if (metric === 'uniqueUsers') return row.uniqueUsers;
  if (metric === 'totalMinutes') return row.totalMinutes ?? 0;
  return row.sessions;
}

const EXPORT_FORMATS = ['PNG', 'PDF', 'SVG'] as const;

export function DailySessionsChart({
  data,
  colors
}: {
  data: UserActivityDailySessionCount[];
  colors: ChartThemeColors;
}) {
  const rows = data.map((p) => ({
    day: formatReportingChartDayLabel(p.date),
    sessions: p.sessionCount
  }));
  return (
    <Chart dataSource={rows} height="100%">
      <CommonSeriesSettings argumentField="day" type="line" point={{ visible: true, size: 7 }} />
      <Series valueField="sessions" name="Sessions" color={colors.series} />
      <ArgumentAxis
        color={colors.label}
        grid={{ visible: true, color: colors.grid }}
        discreteAxisDivisionMode="crossLabels"
        label={{ font: { color: colors.label, size: 11 } }}
      />
      <ValueAxis
        allowDecimals={false}
        visualRange={{ startValue: 0 }}
        color={colors.label}
        grid={{ visible: true, color: colors.grid }}
        label={{ font: { color: colors.label, size: 11 } }}
      />
      <Legend visible={false} />
      <Tooltip enabled />
      <Export enabled formats={[...EXPORT_FORMATS]} fileName="user-activity-daily-sessions" />
    </Chart>
  );
}

export function DailyUniqueUsersChart({
  data,
  colors
}: {
  data: UserActivityDailyUniqueUsers[];
  colors: ChartThemeColors;
}) {
  const rows = data.map((p) => ({
    day: formatReportingChartDayLabel(p.date),
    uniqueUsers: p.uniqueUsers
  }));
  return (
    <Chart dataSource={rows} height="100%">
      <CommonSeriesSettings argumentField="day" type="line" point={{ visible: true, size: 7 }} />
      <Series valueField="uniqueUsers" name="Unique users" color={colors.series} />
      <ArgumentAxis
        color={colors.label}
        grid={{ visible: true, color: colors.grid }}
        discreteAxisDivisionMode="crossLabels"
        label={{ font: { color: colors.label, size: 11 } }}
      />
      <ValueAxis
        allowDecimals={false}
        visualRange={{ startValue: 0 }}
        color={colors.label}
        grid={{ visible: true, color: colors.grid }}
        label={{ font: { color: colors.label, size: 11 } }}
      />
      <Legend visible={false} />
      <Tooltip enabled />
      <Export enabled formats={[...EXPORT_FORMATS]} fileName="user-activity-daily-users" />
    </Chart>
  );
}

export function MultiSeriesTrendChart({
  data,
  colors,
  fileName = 'user-activity-trend'
}: {
  data: UserActivityTrendPoint[];
  colors: ChartThemeColors;
  fileName?: string;
}) {
  const rows = data.map((p) => ({
    label: p.label.includes('-') ? formatReportingChartDayLabel(p.key) : p.label,
    sessions: p.sessions,
    uniqueUsers: p.uniqueUsers,
    totalMinutes: p.totalMinutes,
    averageMinutes: p.averageMinutes ?? 0
  }));
  return (
    <Chart dataSource={rows} height="100%">
      <CommonSeriesSettings argumentField="label" type="line" point={{ visible: true, size: 6 }} />
      <Series valueField="sessions" name="Sessions" color={colors.series} />
      <Series valueField="uniqueUsers" name="Unique users" color={colors.grid} />
      <Series valueField="totalMinutes" name="Total minutes" color={colors.label} />
      <ArgumentAxis
        color={colors.label}
        grid={{ visible: true, color: colors.grid }}
        discreteAxisDivisionMode="crossLabels"
        label={{ font: { color: colors.label, size: 11 } }}
      />
      <ValueAxis
        allowDecimals={false}
        visualRange={{ startValue: 0 }}
        color={colors.label}
        grid={{ visible: true, color: colors.grid }}
        label={{ font: { color: colors.label, size: 11 } }}
      />
      <Legend verticalAlignment="bottom" horizontalAlignment="center" />
      <Tooltip enabled />
      <Export enabled formats={[...EXPORT_FORMATS]} fileName={fileName} />
    </Chart>
  );
}

export function WeeklyActivityChart({
  data,
  colors
}: {
  data: UserActivityWeeklyBucket[];
  colors: ChartThemeColors;
}) {
  const rows = data.map((w) => ({
    week: weekLabel(w.weekStart),
    sessions: w.sessions,
    uniqueUsers: w.uniqueUsers
  }));
  return (
    <Chart dataSource={rows} height="100%">
      <CommonSeriesSettings argumentField="week" type="bar" />
      <Series valueField="sessions" name="Sessions" color={colors.series} />
      <Series valueField="uniqueUsers" name="Unique users" color={colors.grid} />
      <ArgumentAxis
        color={colors.label}
        grid={{ visible: true, color: colors.grid }}
        discreteAxisDivisionMode="crossLabels"
        label={{ font: { color: colors.label, size: 11 } }}
      />
      <ValueAxis
        allowDecimals={false}
        visualRange={{ startValue: 0 }}
        color={colors.label}
        grid={{ visible: true, color: colors.grid }}
        label={{ font: { color: colors.label, size: 11 } }}
      />
      <Legend verticalAlignment="bottom" horizontalAlignment="center" />
      <Tooltip enabled />
      <Export enabled formats={[...EXPORT_FORMATS]} fileName="user-activity-weekly" />
    </Chart>
  );
}

export function WeeklyTrendBarChart({
  data,
  colors
}: {
  data: UserActivityTrendPoint[];
  colors: ChartThemeColors;
}) {
  const rows = data.map((w) => ({
    week: weekLabel(w.key),
    sessions: w.sessions,
    uniqueUsers: w.uniqueUsers,
    totalMinutes: w.totalMinutes
  }));
  return (
    <Chart dataSource={rows} height="100%">
      <CommonSeriesSettings argumentField="week" type="bar" />
      <Series valueField="sessions" name="Sessions" color={colors.series} />
      <Series valueField="uniqueUsers" name="Unique users" color={colors.grid} />
      <Series valueField="totalMinutes" name="Total minutes" color={colors.label} />
      <ArgumentAxis
        color={colors.label}
        grid={{ visible: true, color: colors.grid }}
        discreteAxisDivisionMode="crossLabels"
        label={{ font: { color: colors.label, size: 11 } }}
      />
      <ValueAxis
        allowDecimals={false}
        visualRange={{ startValue: 0 }}
        color={colors.label}
        grid={{ visible: true, color: colors.grid }}
        label={{ font: { color: colors.label, size: 11 } }}
      />
      <Legend verticalAlignment="bottom" horizontalAlignment="center" />
      <Tooltip enabled />
      <Export enabled formats={[...EXPORT_FORMATS]} fileName="user-activity-weekly-trend" />
    </Chart>
  );
}

export function NamedBreakdownBarChart({
  data,
  colors,
  valueField = 'sessions',
  fileName = 'user-activity-breakdown'
}: {
  data: UserActivityNamedCount[] | UserActivityDimensionRow[];
  colors: ChartThemeColors;
  valueField?: UserActivityReportMetric;
  fileName?: string;
}) {
  const rows = [...data]
    .map((r) => ({
      label: r.label,
      value:
        'share' in r
          ? dimensionMetricValue(r, valueField)
          : valueField === 'uniqueUsers'
            ? r.uniqueUsers
            : valueField === 'totalMinutes'
              ? r.totalMinutes ?? 0
              : r.sessions
    }))
    .reverse();
  return (
    <Chart dataSource={rows} height="100%" rotated>
      <CommonSeriesSettings argumentField="label" type="bar" />
      <Series valueField="value" name={metricCaption(valueField)} color={colors.series} />
      <ArgumentAxis color={colors.label} label={{ font: { color: colors.label, size: 11 } }} />
      <ValueAxis
        allowDecimals={false}
        visualRange={{ startValue: 0 }}
        color={colors.label}
        grid={{ visible: true, color: colors.grid }}
        label={{ font: { color: colors.label, size: 11 } }}
      />
      <Legend visible={false} />
      <Tooltip enabled />
      <Export enabled formats={[...EXPORT_FORMATS]} fileName={fileName} />
    </Chart>
  );
}

export function NamedShareDonut({
  data,
  colors: _colors,
  valueField = 'sessions',
  fileName = 'user-activity-share'
}: {
  data: UserActivityNamedCount[] | UserActivityDimensionRow[];
  colors: ChartThemeColors;
  valueField?: UserActivityReportMetric;
  fileName?: string;
}) {
  const rows = data.map((r) => ({
    label: r.label,
    value:
      'share' in r
        ? dimensionMetricValue(r, valueField)
        : valueField === 'uniqueUsers'
          ? r.uniqueUsers
          : valueField === 'totalMinutes'
            ? r.totalMinutes ?? 0
            : r.sessions
  }));
  return (
    <PieChart dataSource={rows} type="doughnut" height="100%" resolveLabelOverlapping="shift">
      <PieSeries argumentField="label" valueField="value">
        <Label visible customizeText={(arg) => `${arg.argumentText}: ${arg.valueText}`}>
          <Connector visible />
        </Label>
      </PieSeries>
      <PieLegend verticalAlignment="bottom" horizontalAlignment="center" />
      <PieTooltip enabled />
      <PieExport enabled formats={[...EXPORT_FORMATS]} fileName={fileName} />
    </PieChart>
  );
}

export function DurationHistogramChart({
  data,
  colors
}: {
  data: UserActivityDurationBand[];
  colors: ChartThemeColors;
}) {
  const rows = data.map((b) => ({ label: b.label, sessions: b.sessions, share: b.share }));
  return (
    <Chart dataSource={rows} height="100%">
      <CommonSeriesSettings argumentField="label" type="bar" />
      <Series valueField="sessions" name="Completed sessions" color={colors.series} />
      <ArgumentAxis
        color={colors.label}
        label={{ font: { color: colors.label, size: 11 } }}
      />
      <ValueAxis
        allowDecimals={false}
        visualRange={{ startValue: 0 }}
        color={colors.label}
        grid={{ visible: true, color: colors.grid }}
        label={{ font: { color: colors.label, size: 11 } }}
      />
      <Legend visible={false} />
      <Tooltip
        enabled
        customizeTooltip={(arg) => {
          const point = arg as { argument?: unknown; value?: unknown };
          const argument = String(point.argument ?? '');
          const value = String(point.value ?? '');
          const share = rows.find((r) => r.label === argument)?.share ?? 0;
          return { text: `${argument}\n${value} sessions (${share}%)` };
        }}
      />
      <Export enabled formats={[...EXPORT_FORMATS]} fileName="user-activity-duration-bands" />
    </Chart>
  );
}

export function HourOfDayChart({
  data,
  colors,
  metric = 'sessions'
}: {
  data: UserActivityHourBucket[];
  colors: ChartThemeColors;
  metric?: UserActivityReportMetric;
}) {
  const rows = data.map((h) => ({
    hour: `${String(h.hour).padStart(2, '0')}:00`,
    value:
      metric === 'uniqueUsers'
        ? h.uniqueUsers
        : metric === 'totalMinutes'
          ? h.totalMinutes
          : h.sessions
  }));
  return (
    <Chart dataSource={rows} height="100%">
      <CommonSeriesSettings argumentField="hour" type="bar" />
      <Series valueField="value" name={metricCaption(metric)} color={colors.series} />
      <ArgumentAxis
        color={colors.label}
        label={{ font: { color: colors.label, size: 11 } }}
      />
      <ValueAxis
        allowDecimals={false}
        visualRange={{ startValue: 0 }}
        color={colors.label}
        grid={{ visible: true, color: colors.grid }}
        label={{ font: { color: colors.label, size: 11 } }}
      />
      <Legend visible={false} />
      <Tooltip enabled />
      <Export enabled formats={[...EXPORT_FORMATS]} fileName="user-activity-hour-of-day" />
    </Chart>
  );
}

export function DayOfWeekChart({
  data,
  colors,
  metric = 'sessions'
}: {
  data: UserActivityWeekdayBucket[];
  colors: ChartThemeColors;
  metric?: UserActivityReportMetric;
}) {
  const rows = data.map((d) => ({
    day: d.label,
    value:
      metric === 'uniqueUsers'
        ? d.uniqueUsers
        : metric === 'totalMinutes'
          ? d.totalMinutes
          : d.sessions
  }));
  return (
    <Chart dataSource={rows} height="100%">
      <CommonSeriesSettings argumentField="day" type="bar" />
      <Series valueField="value" name={metricCaption(metric)} color={colors.series} />
      <ArgumentAxis
        color={colors.label}
        label={{ font: { color: colors.label, size: 11 } }}
      />
      <ValueAxis
        allowDecimals={false}
        visualRange={{ startValue: 0 }}
        color={colors.label}
        grid={{ visible: true, color: colors.grid }}
        label={{ font: { color: colors.label, size: 11 } }}
      />
      <Legend visible={false} />
      <Tooltip enabled />
      <Export enabled formats={[...EXPORT_FORMATS]} fileName="user-activity-day-of-week" />
    </Chart>
  );
}

export function LifecycleStackedChart({
  lifecycle,
  colors
}: {
  lifecycle: {
    completed: number;
    open: number;
    logout: number;
    expired: number;
    superseded: number;
    otherEndReason: number;
  };
  colors: ChartThemeColors;
}) {
  const rows = [
    { label: 'Completed', value: lifecycle.completed },
    { label: 'Open', value: lifecycle.open },
    { label: 'Logout', value: lifecycle.logout },
    { label: 'Expired', value: lifecycle.expired },
    { label: 'Superseded', value: lifecycle.superseded },
    { label: 'Other end', value: lifecycle.otherEndReason }
  ];
  return (
    <Chart dataSource={rows} height="100%" rotated>
      <CommonSeriesSettings argumentField="label" type="bar" />
      <Series valueField="value" name="Sessions" color={colors.series} />
      <ArgumentAxis color={colors.label} label={{ font: { color: colors.label, size: 11 } }} />
      <ValueAxis
        allowDecimals={false}
        visualRange={{ startValue: 0 }}
        color={colors.label}
        grid={{ visible: true, color: colors.grid }}
        label={{ font: { color: colors.label, size: 11 } }}
      />
      <Legend visible={false} />
      <Tooltip enabled />
      <Export enabled formats={[...EXPORT_FORMATS]} fileName="user-activity-lifecycle" />
    </Chart>
  );
}
